// Copyright (c) Bentley Systems
import { useCallback, useEffect, useState } from "react";
import { ConfigManager } from "../Helpers/config-manager";
import { ClientTelemetryOverview, DayFilterValue } from "./Models/on-prem-telemetry-models";

type UseITwinOnPremTelemetryApiProps = {
  accessToken: string;
  iTwinId?: string;
  isEnabled: boolean;
  /** Only runs last run within this many days are fetched; 0 (default) fetches every retained run. Clients are returned regardless. */
  dayFilter?: DayFilterValue;
  /** Changing it re-fetches, e.g. after the caller reloads its client list. */
  reloadKey?: number;
};

/** Shape of one client row as returned by the iTwin-scoped endpoint (camelCase wire contract). */
type ITwinClientResponse = {
  mqttClientName: string;
  installedVersion?: string;
  lastUsed?: string;
  projects?: {
    projectId: string;
    projectName?: string;
    lastRunSuccess?: boolean;
    lastRun: string;
  }[];
};

/**
 * Telemetry of the on-prem clients that executed runs under one iTwin, keyed by client name.
 * Only that iTwin's runs are returned, so health computed from them reflects the iTwin alone.
 * Optional data: failures raise no error, and `isAvailable` stays false so callers can hide it,
 * e.g. where the endpoint is not deployed.
 */
export const useITwinOnPremTelemetryApi = ({
  accessToken,
  iTwinId,
  isEnabled,
  dayFilter = 0,
  reloadKey,
}: UseITwinOnPremTelemetryApiProps) => {
  const [telemetryByClientName, setTelemetryByClientName] = useState<
    Record<string, ClientTelemetryOverview>
  >({});
  const [isAvailable, setIsAvailable] = useState(false);

  const fetchTelemetry = useCallback(
    async (signal: AbortSignal) => {
      if (!isEnabled || !iTwinId) {
        setTelemetryByClientName({});
        setIsAvailable(false);
        return;
      }
      const url = `${ConfigManager.projectProviderUrl}/api/itwins/${encodeURIComponent(iTwinId)}/onprem-telemetry?days=${dayFilter}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: crypto.randomUUID(),
          },
          signal,
        });

        if (!response.ok) {
          setTelemetryByClientName({});
          setIsAvailable(false);
          return;
        }

        const rows: ITwinClientResponse[] = await response.json();
        const map: Record<string, ClientTelemetryOverview> = {};
        for (const row of rows) {
          map[row.mqttClientName] = {
            mqttClientName: row.mqttClientName,
            installedVersion: row.installedVersion,
            lastUsed: row.lastUsed,
            projects: (row.projects ?? []).map((p) => ({
              projectId: p.projectId,
              projectName: p.projectName,
              iTwinId,
              lastRunSuccess: p.lastRunSuccess ?? false,
              lastRun: p.lastRun,
              isMuted: false,
            })),
          };
        }
        setTelemetryByClientName(map);
        setIsAvailable(true);
      } catch (error: unknown) {
        if ((error as { name?: string })?.name !== "AbortError") {
          setTelemetryByClientName({});
          setIsAvailable(false);
        }
      }
    },
    [accessToken, iTwinId, isEnabled, dayFilter],
  );

  useEffect(() => {
    const abortController = new AbortController();
    void fetchTelemetry(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchTelemetry, reloadKey]);

  return { telemetryByClientName, isAvailable };
};
